import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Users,
  Edit3,
  ExternalLink,
  Calendar,
  Layers,
  Sparkles,
  Radio,
  Eye,
  EyeOff,
  CheckCircle2,
  Loader2,
  Trash2,
} from "lucide-react";
import { workshopsService } from "@/api/workshopsService";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

/**
 * ============================================================
 * ADMIN WORKSHOPS MANAGEMENT LIST
 * ============================================================
 * Full workshop dashboard with quick inline toggles for:
 * - Promotional Modal [ON/OFF]
 * - Show Highlights [ON/OFF]
 * - Direct links to applicants, editor, and live public preview
 */
export default function AdminWorkshops() {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadWorkshops = useCallback(() => {
    workshopsService.getAdminWorkshops().then((data) => {
      setWorkshops(data || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadWorkshops();
  }, [loadWorkshops]);

  const handleTogglePromo = async (workshop) => {
    const nextState = !workshop.show_in_promotional_modal;
    try {
      setUpdatingId(`promo-${workshop.id}`);
      await workshopsService.updateWorkshopPromotionSettings(workshop.id, nextState);
      setWorkshops((prev) =>
        prev.map((w) =>
          w.id === workshop.id ? { ...w, show_in_promotional_modal: nextState } : w
        )
      );
      toast.success(
        nextState
          ? `"${workshop.title}" added to Promotional Modal.`
          : `"${workshop.title}" removed from Promotional Modal.`
      );
    } catch (err) {
      console.error("Failed to update promotional modal toggle:", err);
      toast.error("Failed to update promotional modal setting");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleHighlights = async (workshop) => {
    const nextState = !workshop.show_highlights;
    try {
      setUpdatingId(`highlights-${workshop.id}`);
      await workshopsService.updateWorkshopHighlightsSettings(workshop.id, nextState);
      setWorkshops((prev) =>
        prev.map((w) =>
          w.id === workshop.id ? { ...w, show_highlights: nextState } : w
        )
      );
      toast.success(
        nextState
          ? `Session highlights enabled for "${workshop.title}".`
          : `Session highlights disabled for "${workshop.title}".`
      );
    } catch (err) {
      console.error("Failed to update highlights toggle:", err);
      toast.error("Failed to update highlights setting");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteWorkshop = async (workshop) => {
    const confirmMessage =
      `Are you sure you want to COMPLETELY delete "${workshop.title}"?\n\n` +
      `This will permanently remove the workshop along with all its agenda, instructors, highlights, media, and applicant registrations.\n\n` +
      `This action CANNOT be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingId(workshop.id);
      await workshopsService.deleteWorkshop(workshop.id, { permanent: true });
      setWorkshops((prev) => prev.filter((w) => w.id !== workshop.id));
      toast.success(`Workshop "${workshop.title}" completely deleted.`);
    } catch (err) {
      console.error("Failed to delete workshop:", err);
      toast.error(err.message || "Failed to completely delete workshop");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      {/* Top action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Workshops Management</h1>
          <p className="text-xs text-slate-600 mt-1 font-medium">
            Manage public listings, schedules, curriculums, highlights, and student applications.
          </p>
        </div>

        <Button
          onClick={() => navigate("/admin/workshops/new")}
          className="rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 h-9 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Create Workshop</span>
        </Button>
      </div>

      {/* Metrics strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
          <div className="text-xs text-slate-500 font-medium mb-1">Total Workshops</div>
          <div className="text-2xl font-bold text-slate-900">{workshops.length}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
          <div className="text-xs text-slate-500 font-medium mb-1">Published</div>
          <div className="text-2xl font-bold text-emerald-600">
            {workshops.filter((w) => w.status === "Published").length}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
          <div className="text-xs text-slate-500 font-medium mb-1">In Promo Modal</div>
          <div className="text-2xl font-bold text-cyan-600">
            {workshops.filter((w) => w.show_in_promotional_modal).length}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
          <div className="text-xs text-slate-500 font-medium mb-1">Total Registrations</div>
          <div className="text-2xl font-bold text-slate-900">
            {workshops.reduce((acc, w) => acc + (w.registration_count || 0), 0)}
          </div>
        </div>
      </div>

      {/* Workshops list */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xs">
        <div className="p-4 border-b border-slate-200 bg-slate-50 items-center ">
          <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            All Workshops ({workshops.length})
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-xs text-slate-400 font-medium flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-600" />
            <span>Loading workshops...</span>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {workshops.map((workshop) => {
              const isPromoUpdating = updatingId === `promo-${workshop.id}`;
              const isHighlightsUpdating = updatingId === `highlights-${workshop.id}`;

              return (
                <div
                  key={workshop.id}
                  className="p-5 flex flex-col xl:flex-row xl:items-center justify-between gap-5 hover:bg-slate-50/80 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span
                        className={cn(
                          "text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider",
                          workshop.status === "Published"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : workshop.status === "Draft"
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                        )}
                      >
                        {workshop.status}
                      </span>

                      {/* Temporal Lifecycle Tag */}
                      {workshop.lifecycle_stage === "live" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
                          <Radio className="w-2.5 h-2.5 animate-pulse text-rose-600" />
                          <span>Live Now</span>
                        </span>
                      )}
                      {workshop.lifecycle_stage === "completed_by_schedule" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">
                          Ended by Schedule
                        </span>
                      )}
                      {workshop.lifecycle_stage === "upcoming" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-cyan-50 text-cyan-700 border border-cyan-200">
                          Upcoming
                        </span>
                      )}

                      <span className="text-xs text-slate-500 font-medium">{workshop.category}</span>
                      <span className="text-xs text-slate-300">•</span>
                      <span className="text-xs text-slate-500 font-medium">{workshop.mode}</span>
                    </div>

                    <h3 className="text-sm font-semibold text-slate-900 truncate mb-1">
                      {workshop.title}
                    </h3>

                    <div className="flex items-center gap-4 text-xs text-slate-500 font-medium flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-cyan-600" />
                        <span>{workshop.date} ({workshop.start_time} - {workshop.end_time})</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-cyan-600" />
                        <span>
                          {workshop.registration_count || 0} / {workshop.capacity} Enrolled
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Inline Settings & Actions Strip */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Promotional Modal Quick Toggle */}
                    <button
                      type="button"
                      disabled={isPromoUpdating}
                      onClick={() => handleTogglePromo(workshop)}
                      title="Toggle whether this workshop appears in the global promotional modal"
                      className={cn(
                        "h-8 px-2.5 rounded-xl text-xs font-semibold border flex items-center gap-1.5 transition-colors",
                        workshop.show_in_promotional_modal
                          ? "bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100"
                          : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900"
                      )}
                    >
                      {isPromoUpdating ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600" />
                      ) : (
                        <Sparkles className={cn("w-3.5 h-3.5", workshop.show_in_promotional_modal ? "text-cyan-600" : "text-slate-400")} />
                      )}
                      <span>Promo Modal:</span>
                      <span className={cn("font-bold", workshop.show_in_promotional_modal ? "text-cyan-800" : "text-slate-500")}>
                        {workshop.show_in_promotional_modal ? "ON" : "OFF"}
                      </span>
                    </button>

                    {/* Show Highlights Quick Toggle */}
                    <button
                      type="button"
                      disabled={isHighlightsUpdating}
                      onClick={() => handleToggleHighlights(workshop)}
                      title="Toggle post-event session highlights visibility"
                      className={cn(
                        "h-8 px-2.5 rounded-xl text-xs font-semibold border flex items-center gap-1.5 transition-colors",
                        workshop.show_highlights
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900"
                      )}
                    >
                      {isHighlightsUpdating ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                      ) : (
                        <CheckCircle2 className={cn("w-3.5 h-3.5", workshop.show_highlights ? "text-emerald-600" : "text-slate-400")} />
                      )}
                      <span>Highlights:</span>
                      <span className={cn("font-bold", workshop.show_highlights ? "text-emerald-800" : "text-slate-500")}>
                        {workshop.show_highlights ? "ON" : "OFF"}
                      </span>
                    </button>

                    {/* Action Links */}
                    <div className="flex items-center gap-1.5 ml-auto sm:ml-0">
                      <Link to={`/admin/workshops/${workshop.slug || workshop.id}/registrations`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 gap-1.5 font-medium"
                        >
                          <Users className="w-3.5 h-3.5 text-cyan-600" />
                          <span>Applicants ({workshop.registration_count || 0})</span>
                        </Button>
                      </Link>

                      <Link to={`/admin/workshops/${workshop.slug || workshop.id}/edit`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 gap-1.5 font-medium"
                        >
                          <Edit3 className="w-3.5 h-3.5 text-cyan-600" />
                          <span>Edit</span>
                        </Button>
                      </Link>

                      <Link to={`/workshops/${workshop.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-slate-400 hover:text-slate-800 hover:bg-slate-100 px-2"
                          title="Preview public page"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </Link>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteWorkshop(workshop)}
                        disabled={deletingId === workshop.id}
                        className="h-8 text-xs border-rose-200 bg-rose-50/60 text-rose-700 hover:bg-rose-100 hover:text-rose-800 hover:border-rose-300 gap-1.5 font-medium transition-colors"
                        title="Completely delete workshop"
                      >
                        {deletingId === workshop.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-600" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        )}
                        <span>Delete</span>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
